import { Navigate } from 'react-router-dom'

import { apiQueryClient } from 'features/api/apiQueryClient'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { HOURS } from '@sequencehq/utils/dates'

export const preloadCurrencies = () => {
  apiQueryClient.prefetchQuery({
    queryKey: dashboard20240730Client.getSequenceAccountSettings.queryKey,
    queryFn: async () => {
      return (
        await dashboard20240730Client.getSequenceAccountSettings.request()
      )?.data
    },
    staleTime: 1 * HOURS
  })
}

// Preloads the customer custom-property keys backing the
// CUSTOMER.CUSTOM_PROPERTY switch-condition key selector, so the dropdown is
// populated immediately when the user reaches the condition editor.
export const preloadCustomerCustomPropertyKeys = () => {
  apiQueryClient.prefetchQuery({
    queryKey: dashboard20240730Client.getCustomerCustomPropertyKeys.queryKey,
    queryFn: async () => {
      return (
        await dashboard20240730Client.getCustomerCustomPropertyKeys.request()
      )?.data
    },
    staleTime: 1 * HOURS
  })
}

// Preloads the quote templates backing the QUOTE_TEMPLATE switch-condition
// picker, so the dropdown is populated immediately when the user reaches the
// condition editor.
export const preloadQuoteTemplates = () => {
  apiQueryClient.prefetchQuery({
    queryKey: dashboard20240730Client.getListQuoteTemplates.queryKey,
    queryFn: async () => {
      return (await dashboard20240730Client.getListQuoteTemplates.request())
        ?.data
    },
    staleTime: 1 * HOURS
  })
}

// Preloads the account's own payment terms, which join the presets the
// PAYMENT_TERMS switch-condition value selector offers.
export const preloadMerchantSettings = () => {
  apiQueryClient.prefetchQuery({
    queryKey: dashboard20240730Client.getMerchantForSequenceAccount.queryKey,
    queryFn: async () => {
      return (
        await dashboard20240730Client.getMerchantForSequenceAccount.request()
      )?.data
    },
    staleTime: 1 * HOURS
  })
}

export const automationsPaths = {
  manualTasksInbox: () => '/watchtower/inbox',
  manualTaskInboxPreview: (taskId: string) =>
    `/watchtower/inbox/tasks/${taskId}`,
  notificationInboxPreview: (notificationId: string) =>
    `/watchtower/inbox/notifications/${notificationId}`,
  executionInbox: () => `/watchtower/inbox/all`,
  allTaskInboxPreview: (taskId: string) =>
    `/watchtower/inbox/all/tasks/${taskId}`,
  allNotificationInboxPreview: (notificationId: string) =>
    `/watchtower/inbox/all/notifications/${notificationId}`,
  automationsList: () => '/workflows/management',
  executionHistory: () => '/workflows/management/history',
  executionHistoryPreview: (executionId: string) =>
    `/workflows/management/history/${executionId}`,
  automationsEditor: ({ automationId }: { automationId: string }) =>
    `/workflows/editor/${automationId}`
}

export const automationsRoutes = [
  {
    path: '/watchtower',
    children: [
      {
        index: true,
        element: <Navigate to="/watchtower/inbox" />
      },
      {
        path: 'inbox',
        lazy: async () => {
          const { Inbox } = await import('./watchtower')
          return { Component: Inbox }
        },
        children: [
          {
            path: 'tasks',
            children: [
              {
                path: ':taskId',
                element: null
              }
            ]
          },
          {
            path: 'notifications',
            children: [
              {
                path: ':notificationId',
                element: null
              }
            ]
          },
          {
            path: 'all',
            children: [
              { path: 'tasks', children: [{ path: ':taskId', element: null }] },
              {
                path: 'notifications',
                children: [{ path: ':notificationId', element: null }]
              }
            ]
          }
        ]
      }
    ]
  },
  {
    path: '/workflows',
    loader: () => {
      preloadCurrencies()
      preloadCustomerCustomPropertyKeys()
      preloadQuoteTemplates()
      preloadMerchantSettings()
      return true
    },
    children: [
      {
        index: true,
        element: <Navigate to="/workflows/management" />
      },
      {
        path: 'management',
        lazy: async () => {
          const { AutomationsManagement } = await import(
            './automationsManagement'
          )
          return { Component: AutomationsManagement }
        },
        children: [
          {
            path: 'history',
            children: [{ path: ':executionId', element: null }]
          }
        ]
      },
      {
        path: 'editor/:automationId',
        lazy: async () => {
          const { AutomationEditor } = await import('./automationEditor')
          return { Component: AutomationEditor }
        }
      }
    ]
  }
]
